import type { PlaylistItem } from '~/services/scheduler/types';

/**
 * Constrói pseudo-`PlaylistItem`s para a mídia dos elementos `MEDIA_OVERLAY`
 * (conteúdo sobreposto — ex: moldura PNG por cima do vídeo).
 *
 * Permite que a mídia do overlay seja **pré-cacheada** junto com o conteúdo
 * principal (via `mediaStore.syncWithSchedule` + `prefetchAll`), em vez de
 * baixada on-demand a cada exibição. Resultado: overlay aparece instantâneo +
 * funciona offline. É player-only — NÃO muda o contrato server↔player (a mídia
 * já vem presigned no sync).
 *
 * Regras:
 * - Keya por `media_content_id` (ESTÁVEL), não pela `media_url` presigned
 *   (volátil — muda a cada sync; o cache trata refresh via `getLocalUrlAsync`).
 * - Pula itens sem id/url, os que já são conteúdo da playlist (já cacheados sob
 *   o mesmo id), e deduplica o mesmo overlay referenciado por vários conteúdos.
 */
export function buildOverlayCacheItems(
  playlistItems: PlaylistItem[],
): PlaylistItem[] {
  const playlistIds = new Set(playlistItems.map((i) => i.id));
  const byId = new Map<string, PlaylistItem>();

  for (const item of playlistItems) {
    if (!item.elementos) continue;
    for (const el of item.elementos) {
      if (el.tipo !== 'MEDIA_OVERLAY') continue;
      const id = el.media_content_id;
      const url = el.media_url;
      if (!id || !url) continue; // sem id/url presigned → não cacheável
      if (playlistIds.has(id)) continue; // já é conteúdo da playlist
      if (byId.has(id)) continue; // dedup multi-referência

      byId.set(id, {
        id,
        type: el.media_type === 'video' ? 'video' : 'image',
        name: `overlay:${id.slice(0, 8)}`,
        url,
        duration: 0,
        fileSize: 0,
        checksum: '',
        mimeType: el.media_mime_type ?? '',
      });
    }
  }

  return Array.from(byId.values());
}

export default buildOverlayCacheItems;
